import React, { useCallback, useEffect, useState } from "react"
import { PlayCircleIcon } from "@heroicons/react/24/solid"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import ImagePlaceholder from "../ImagePlaceholder"

type TClasses = Partial<{
  image: string
  wrapper: string
}>

export type TImageProps = {
  /** When empty/undefined, the fallback or ImagePlaceholder is rendered. */
  src?: string
  alt: string
  size?: number
  width?: number
  height?: number
  classes?: Partial<TClasses>
  fallback?: React.ReactNode | string
  loadingIndicator?: React.ReactNode
  /**
   * When false, video sources render as a static, non-interactive preview
   * (first frame + play badge) instead of a playable player.
   */
  allowVideoPlayback?: boolean
  /**
   * Explicit media type. Skips URL extension sniffing — required for
   * extensionless URLs (e.g. AMS-resolved S3 assets) where sniffing
   * always falls back to "img".
   */
  mediaType?: "img" | "video"
} & Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src" | "alt" | "className">

export const Image: React.FC<TImageProps> = ({
  src,
  alt,
  size,
  width,
  height,
  classes,
  fallback,
  loadingIndicator,
  allowVideoPlayback = true,
  mediaType,
  ...props
}) => {
  const [isBrokenImage, setIsBrokenImage] = useState(!src)
  const [isFallbackBrokenImage, setIsFallbackBrokenImage] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Reset broken/loading state when src changes (e.g., async resolve completes)
  useEffect(() => {
    setIsBrokenImage(!src)
    setIsFallbackBrokenImage(false)
    setIsLoading(true)
  }, [src])

  const contentSniffer = (contentUrl: string | undefined) => {
    if (!contentUrl) return "img"
    const types = new Map([
      ["jpg", "img"],
      ["gif", "img"],
      ["mp4", "video"],
      ["3gp", "video"],
      ["mov", "video"],
      ["webm", "video"],
    ])

    try {
      const url = new URL(contentUrl)
      // Last dot wins: CDN paths like /v/t15.5256-10/foo.mp4 have dots in
      // earlier segments, so split(".")[1] mis-sniffed every such video.
      const segments = url.pathname.split(".")
      const extension =
        segments.length > 1 ? segments[segments.length - 1].toLowerCase() : ""

      const type = types.get(extension)

      return type || "img"
    } catch (error) {
      return "img"
    }
  }

  const handleLoad = () => {
    setIsLoading(false)
  }

  const handleFallbackError = () => {
    setIsLoading(false)
    setIsFallbackBrokenImage(true)
  }

  const fallbackImageProps: React.ImgHTMLAttributes<HTMLImageElement> = {
    alt,
    style: {
      width: size || width,
      height,
    },
    onLoad: handleLoad,
    onError: handleFallbackError,
    className: cn(`object-cover`, classes?.image),
    ...props,
  }

  const renderFallback = useCallback(() => {
    if (!isFallbackBrokenImage && fallback) {
      if (typeof fallback === "string") {
        return <img src={fallback} {...fallbackImageProps} />
      }
      return fallback
    }
    return <ImagePlaceholder />
  }, [size, width, height, fallback, fallbackImageProps, isFallbackBrokenImage])

  const handleError = () => {
    setIsBrokenImage(true)
  }

  const mediaProps: React.ImgHTMLAttributes<HTMLImageElement> = {
    src,
    alt,
    style: {
      width: size || width,
      height,
    },
    onLoad: handleLoad,
    onError: handleError,
    className: cn(`object-cover`, classes?.image),
    ...props,
  }

  const renderImageContent = useCallback(() => {
    // No src at all → show the fallback/placeholder instead of a broken <img>.
    if (!src) {
      return renderFallback()
    }
    if (isLoading && loadingIndicator) {
      return loadingIndicator
    } else if (isBrokenImage) {
      return renderFallback()
    }

    return <img {...mediaProps} />
  }, [
    isLoading,
    loadingIndicator,
    isBrokenImage,
    isFallbackBrokenImage,
    mediaProps,
    src,
    fallback,
  ])

  const renderVideoContent = useCallback(() => {
    if (isLoading && loadingIndicator) {
      return loadingIndicator
    } else if (isBrokenImage) {
      return renderFallback()
    }

    if (!allowVideoPlayback) {
      return (
        <>
          <video
            muted
            preload="metadata"
            className={cn(
              "h-full w-full rounded-lg object-cover pointer-events-none",
              classes?.image,
            )}
            src={src}
          />
          <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <PlayCircleIcon className="w-12 h-12 text-white/80 drop-shadow-lg" />
          </span>
        </>
      )
    }

    return (
      <video
        controls
        className={cn("h-full w-full rounded-lg", classes?.image)}
        src={src}
      />
    )
  }, [
    isLoading,
    loadingIndicator,
    isBrokenImage,
    isFallbackBrokenImage,
    mediaProps,
    src,
    allowVideoPlayback,
  ])

  const type = mediaType ?? contentSniffer(src)

  return (
    <span
      className={cn(
        "relative flex items-center justify-center",
        classes?.wrapper,
      )}
      onClick={props.onClick}
    >
      {type === "img" && renderImageContent()}
      {type === "video" && renderVideoContent()}
    </span>
  )
}
